/**
 * Lender Registration Constants
 * All static data for the lender registration form
 */

export interface LocalizedOption {
  key: string;
  en: string;
  fr: string;
}

export interface FinancingAmount {
  value: string;
  en: string;
  fr: string;
}

// Loan Product Types
export const LOAN_PRODUCT_TYPES: LocalizedOption[] = [
  { key: 'senior_debt_lbo', en: 'Senior Debt (LBO)', fr: 'Dette Senior (LBO)' },
  { key: 'unitranche', en: 'Unitranche', fr: 'Unitranche' },
  { key: 'commercial_loan', en: 'Commercial Loan', fr: 'Prêt Commercial' },
  { key: 'sba_loan', en: 'SBA Loan', fr: 'SBA Loan' },
  { key: 'abl', en: 'Asset-Based Lending (ABL)', fr: 'Asset-Based Lending (ABL)' },
  { key: 'seller_note', en: 'Seller Note (Subordinated Debt)', fr: 'Prêt Vendeur (Dette Subordonnée)' },
  { key: 'mezzanine', en: 'Mezzanine', fr: 'Mezzanine' },
  { key: 'equipment_lease', en: 'Equipment Lease', fr: 'Equipment Lease' },
  { key: 'real_estate_loan', en: 'Real Estate Loan', fr: 'Real Estate Loan' },
  { key: 'bridge', en: 'Bridge Loan', fr: 'Bridge Loan' },
  { key: 'other_loan', en: 'Other Loan Type', fr: 'Autre Type de Prêt' },
];

// Lender Types
export const LENDER_TYPES: LocalizedOption[] = [
  { key: 'sba_preferred_lender', en: 'SBA Preferred Lender', fr: 'SBA Preferred Lender' },
  { key: 'traditional_bank', en: 'Traditional Bank', fr: 'Traditional Bank' },
  { key: 'non_bank_lender', en: 'Non-Bank Lender', fr: 'Non-Bank Lender' },
  { key: 'fintech_lender', en: 'FinTech Lender', fr: 'FinTech Lender' },
  { key: 'private_lender', en: 'Private Lender', fr: 'Private Lender' },
  { key: 'private_equity', en: 'Private Equity (PE)', fr: 'Private Equity (PE)' },
  { key: 'family_office', en: 'Family Office', fr: 'Family Office' },
  { key: 'equipment_financing', en: 'Equipment Financing', fr: 'Equipment Financing' },
  { key: 'asset_based_lender', en: 'Asset-Based Lender (ABL)', fr: 'Asset-Based Lender (ABL)' },
  { key: 'other_lender', en: 'Other Lender Type', fr: 'Autre Type de Prêteur' },
];

// Collateral Options
export const COLLATERAL_OPTIONS: LocalizedOption[] = [
  { key: 'none', en: 'None', fr: 'Aucun' },
  { key: 'all_business_assets_first_lien', en: 'All Business Assets (First Lien)', fr: 'Tous les Actifs (Premier Rang)' },
  { key: 'accounts_receivable', en: 'Accounts Receivable', fr: 'Comptes Clients' },
  { key: 'inventory', en: 'Inventory', fr: 'Inventaire' },
  { key: 'equipment_and_machinery', en: 'Equipment & Machinery', fr: 'Équipement & Machinerie' },
  { key: 'personal_real_estate', en: 'Personal Real Estate', fr: 'Immobilier Personnel' },
  { key: 'personal_guarantee_secured', en: 'Personal Guarantee (Secured)', fr: 'Garantie Personnelle (Sécurisée)' },
  { key: 'personal_guarantee_unsecured', en: 'Personal Guarantee (Unsecured)', fr: 'Garantie Personnelle (Non Sécurisée)' },
  { key: 'business_real_estate_first_lien', en: 'Business Real Estate (First Lien)', fr: 'Immobilier Commercial (Premier Rang)' },
  { key: 'intellectual_property', en: 'Intellectual Property', fr: 'Propriété Intellectuelle' },
  { key: 'cash_on_deposit', en: 'Cash on Deposit', fr: 'Dépôts en Espèces' },
];

// Financing Amounts
export const FINANCING_AMOUNTS: FinancingAmount[] = [
  { value: '0', en: 'Less than 500k USD', fr: 'Moins de 500k USD' },
  { value: '500000', en: '500k USD', fr: '500k USD' },
  { value: '1000000', en: '1M USD', fr: '1M USD' },
  { value: '2000000', en: '2M USD', fr: '2M USD' },
  { value: '5000000', en: '5M USD', fr: '5M USD' },
  { value: '10000000', en: '10M USD', fr: '10M USD' },
  { value: '20000000', en: '20M USD', fr: '20M USD' },
  { value: '30000000', en: '30M USD', fr: '30M USD' },
  { value: '40000000', en: '40M USD', fr: '40M USD' },
  { value: '50000000', en: '50M USD and above', fr: '50M USD et plus' },
];

// Canadian Provinces
export const CANADIAN_PROVINCES = {
  en: [
    'All', 'Alberta', 'British Columbia', 'Prince Edward Island', 'Manitoba',
    'New Brunswick', 'Nova Scotia', 'Nunavut', 'Ontario',
    'Quebec', 'Saskatchewan', 'Newfoundland and Labrador', 'Northwest Territories', 'Yukon'
  ],
  fr: [
    'Tout', 'Alberta', 'Colombie-Britannique', 'Île-du-Prince-Édouard', 'Manitoba',
    'Nouveau-Brunswick', 'Nouvelle-Écosse', 'Nunavut', 'Ontario',
    'Québec', 'Saskatchewan', 'Terre-Neuve-et-Labrador', 'Territoires du Nord-Ouest', 'Yukon'
  ]
};

// US States
export const US_STATES = [
  'All', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// European Countries
export const EUROPEAN_COUNTRIES = {
  en: [
    'All', 'Germany', 'Austria', 'Belgium', 'Bulgaria', 'Cyprus', 'Croatia', 'Denmark',
    'Spain', 'Estonia', 'Finland', 'France', 'Greece', 'Hungary', 'Ireland', 'Italy',
    'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal',
    'Czech Republic', 'Romania', 'United Kingdom', 'Slovakia', 'Slovenia', 'Sweden',
    'Switzerland', 'Norway', 'Iceland'
  ],
  fr: [
    'Tout', 'Allemagne', 'Autriche', 'Belgique', 'Bulgarie', 'Chypre', 'Croatie', 'Danemark',
    'Espagne', 'Estonie', 'Finlande', 'France', 'Grèce', 'Hongrie', 'Irlande', 'Italie',
    'Lettonie', 'Lituanie', 'Luxembourg', 'Malte', 'Pays-Bas', 'Pologne', 'Portugal',
    'République tchèque', 'Roumanie', 'Royaume-Uni', 'Slovaquie', 'Slovénie', 'Suède',
    'Suisse', 'Norvège', 'Islande'
  ]
};

// Other Countries
export const OTHER_COUNTRIES = {
  en: [
    'All', 'South Africa', 'Saudi Arabia', 'Argentina', 'Australia', 'Brazil', 'Chile',
    'China', 'Colombia', 'South Korea', 'Egypt', 'United Arab Emirates', 'India',
    'Indonesia', 'Israel', 'Japan', 'Morocco', 'Mexico', 'Nigeria', 'New Zealand',
    'Peru', 'Philippines', 'Russia', 'Singapore', 'Thailand', 'Turkey', 'Vietnam'
  ],
  fr: [
    'Tout', 'Afrique du Sud', 'Arabie Saoudite', 'Argentine', 'Australie', 'Brésil', 'Chili',
    'Chine', 'Colombie', 'Corée du Sud', 'Égypte', 'Émirats Arabes Unis', 'Inde',
    'Indonésie', 'Israël', 'Japon', 'Maroc', 'Mexique', 'Nigéria', 'Nouvelle-Zélande',
    'Pérou', 'Philippines', 'Russie', 'Singapour', 'Thaïlande', 'Turquie', 'Vietnam'
  ]
};

// UI Translations
export const LENDER_REGISTRATION_TRANSLATIONS = {
  en: {
    title: 'Lender Partner Registration Questionnaire',
    subtitle: 'Please complete all 4 steps to finalize your registration with the AcquizGroup network.',
    step1: {
      title: 'Step 1: Contact Information & Account',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email (with verification)',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      companyName: 'Company / Institution Name',
      address: 'Full Address (Headquarters)',
      phoneNumber: 'Phone Number',
      passwordPlaceholder: 'Minimum 8 characters',
      confirmPasswordPlaceholder: 'Re-enter your password',
    },
    step2: {
      title: 'Step 2: Investment Profile',
      lenderTypes: 'Company Type (Multiple Selection)',
      loanProductTypes: 'Loan Products / Financial Products Offered',
      collateralOptions: 'Collateral Accepted (Domestic)',
      selectionCriteria: 'Selection Criteria (Minimum and Maximum in USD)',
      financingAmount: 'Financing Amount',
      minAmount: 'Minimum Amount (USD)',
      maxAmount: 'Maximum Amount (USD)',
      grossRevenue: 'Gross Revenue',
      minGrossRevenue: 'Minimum Gross Revenue (USD)',
      maxGrossRevenue: 'Maximum Gross Revenue (USD)',
      ebitda: 'EBITDA',
      minEbitda: 'Minimum EBITDA (USD)',
      maxEbitda: 'Maximum EBITDA (USD)',
      cashFlow: 'Cash Flow',
      minCashFlow: 'Minimum Cash Flow (USD)',
      maxCashFlow: 'Maximum Cash Flow (USD)',
      dscr: 'Debt Service Coverage Ratio (DSCR)',
      dscrLabel: 'Debt Service Coverage Ratio',
      dscrPlaceholder: 'e.g., 1.25',
      dscrHelp: "The DSCR measures a company's ability to cover its debt payments. A ratio of 1.25 means the company generates 25% more than required to cover its debts.",
    },
    step3: {
      title: 'Step 3: Jurisdictions and International Criteria',
      note: 'This section concerns your operations and criteria for the jurisdictions where you operate.',
      jurisdictionsTitle: 'Jurisdictions Where You Operate (Multiple Selection by Region)',
      canadaRegion: 'CANADA Region',
      usaRegion: 'USA Region',
      europeRegion: 'EUROPE Region',
      otherRegion: 'OTHER Region',
      multiSelectHelp: 'Hold Ctrl (Windows) or Cmd (Mac) to select multiple options',
      creditScoreRequired: 'Do You Require Credit Score?',
      foreignCreditScoreAccepted: 'Do You Accept Foreign Credit Scores?',
      acceptedCreditJurisdictions: 'Accepted Credit Score Jurisdictions',
      foreignCollateralAccepted: 'Do You Accept Foreign Collateral?',
      foreignCollateralTypes: 'Types of Foreign Collateral Accepted',
      yes: 'Yes',
      no: 'No',
    },
    step4: {
      title: 'Step 4: Terms and Conditions',
      important: 'Please read the terms and conditions carefully before accepting.',
      agreeTerms: 'I accept the Lender Partnership terms and conditions.',
      agreeTermsHelp: 'Please read the terms and conditions above carefully before accepting.',
      backgroundCheck: 'I authorize AcquizGroup to conduct a company/license verification (Background Check).',
      backgroundCheckHelp: 'This verification is mandatory for all lenders according to Annex D, article 1.',
    },
    buttons: {
      next: 'Continue',
      back: 'Back',
      submit: 'Submit Registration',
      submitting: 'Submitting...',
      backToLogin: 'Back to Login',
      newRegistration: 'New Registration',
    },
    success: {
      title: 'Registration Successfully Submitted!',
      message: 'Thank you',
      from: 'from',
      nextSteps: 'Next Steps:',
      step1: 'Background Check in progress',
      step2: 'Confirmation email sent to',
      step3: 'Platform access within 2-3 business days',
    },
    errors: {
      required: 'This field is required.',
      invalidEmail: 'Invalid email format.',
      passwordLength: 'Password must be at least 8 characters.',
      passwordMismatch: 'Passwords do not match.',
      invalidNumber: 'Please enter a valid number.',
      selectCreditJurisdiction: 'Please select at least one jurisdiction for credit scores.',
      selectCollateral: 'Please select at least one foreign collateral type.',
    },
    select: 'Select...',
  },
  fr: {
    title: 'Questionnaire d\'Inscription Partenaire Prêteur',
    subtitle: 'Veuillez compléter les 4 étapes pour finaliser votre inscription au réseau AcquizGroup.',
    step1: {
      title: 'Étape 1: Informations de Contact & Compte',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      email: 'Courriel (avec vérification)',
      password: 'Mot de Passe',
      confirmPassword: 'Confirmer le Mot de Passe',
      companyName: 'Nom de l\'Entreprise / Institution',
      address: 'Adresse Complète (Siège Social)',
      phoneNumber: 'Numéro de Téléphone',
      passwordPlaceholder: 'Minimum 8 caractères',
      confirmPasswordPlaceholder: 'Ressaisissez votre mot de passe',
    },
    step2: {
      title: 'Étape 2: Profil d\'Investissement',
      lenderTypes: 'Type d\'Entreprise (Sélection Multiple)',
      loanProductTypes: 'Produits de Prêt / Produits Financiers Offerts',
      collateralOptions: 'Collatéral Accepté (National)',
      selectionCriteria: 'Critères de Sélection (Minimum et Maximum en USD)',
      financingAmount: 'Montant de Financement',
      minAmount: 'Montant Minimum (USD)',
      maxAmount: 'Montant Maximum (USD)',
      grossRevenue: 'Revenu Brut',
      minGrossRevenue: 'Revenu Brut Minimum (USD)',
      maxGrossRevenue: 'Revenu Brut Maximum (USD)',
      ebitda: 'EBITDA',
      minEbitda: 'EBITDA Minimum (USD)',
      maxEbitda: 'EBITDA Maximum (USD)',
      cashFlow: 'Flux de Trésorerie',
      minCashFlow: 'Flux de Trésorerie Minimum (USD)',
      maxCashFlow: 'Flux de Trésorerie Maximum (USD)',
      dscr: 'Ratio de Couverture du Service de la Dette (DSCR)',
      dscrLabel: 'Ratio de Couverture du Service de la Dette',
      dscrPlaceholder: 'ex: 1.25',
      dscrHelp: 'Le DSCR mesure la capacité d\'une entreprise à couvrir ses paiements de dette. Un ratio de 1.25 signifie que l\'entreprise génère 25% de plus que nécessaire pour couvrir ses dettes.',
    },
    step3: {
      title: 'Étape 3: Juridictions et Critères Internationaux',
      note: 'Cette section concerne vos opérations et critères pour les juridictions où vous opérez.',
      jurisdictionsTitle: 'Juridictions où Vous Opérez (Sélection Multiple par Région)',
      canadaRegion: 'Région CANADA',
      usaRegion: 'Région USA',
      europeRegion: 'Région EUROPE',
      otherRegion: 'Région AUTRES',
      multiSelectHelp: 'Maintenez Ctrl (Windows) ou Cmd (Mac) pour sélectionner plusieurs options',
      creditScoreRequired: 'Exigez-vous une Cote de Crédit?',
      foreignCreditScoreAccepted: 'Acceptez-vous les Cotes de Crédit Étrangères?',
      acceptedCreditJurisdictions: 'Juridictions de Cote de Crédit Acceptées',
      foreignCollateralAccepted: 'Acceptez-vous le Collatéral Étranger?',
      foreignCollateralTypes: 'Types de Collatéral Étranger Acceptés',
      yes: 'Oui',
      no: 'Non',
    },
    step4: {
      title: 'Étape 4: Termes et Conditions',
      important: 'Veuillez lire attentivement les termes et conditions avant d\'accepter.',
      agreeTerms: 'J\'accepte les termes et conditions du partenariat prêteur.',
      agreeTermsHelp: 'Veuillez lire attentivement les termes et conditions ci-dessus avant d\'accepter.',
      backgroundCheck: 'J\'autorise AcquizGroup à effectuer une vérification d\'entreprise/licence (Vérification des Antécédents).',
      backgroundCheckHelp: 'Cette vérification est obligatoire pour tous les prêteurs selon l\'Annexe D, article 1.',
    },
    buttons: {
      next: 'Continuer',
      back: 'Retour',
      submit: 'Soumettre l\'Inscription',
      submitting: 'Soumission...',
      backToLogin: 'Retour à la Connexion',
      newRegistration: 'Nouvelle Inscription',
    },
    success: {
      title: 'Inscription Soumise avec Succès!',
      message: 'Merci',
      from: 'de',
      nextSteps: 'Prochaines Étapes:',
      step1: 'Vérification des antécédents en cours',
      step2: 'Courriel de confirmation envoyé à',
      step3: 'Accès à la plateforme dans 2-3 jours ouvrables',
    },
    errors: {
      required: 'Ce champ est obligatoire.',
      invalidEmail: 'Format d\'email invalide.',
      passwordLength: 'Le mot de passe doit contenir au moins 8 caractères.',
      passwordMismatch: 'Les mots de passe ne correspondent pas.',
      invalidNumber: 'Veuillez entrer un nombre valide.',
      selectCreditJurisdiction: 'Veuillez sélectionner au moins une juridiction pour les côtes de crédit.',
      selectCollateral: 'Veuillez sélectionner au moins un type de collatéral étranger.',
    },
    select: 'Sélectionner...',
  },
};

// Terms and Conditions
export const TERMS_AND_CONDITIONS = {
  en: `
    <p class="text-sm text-gray-600 mb-4"><strong>Version: 2025-09-17 Rev. E1</strong></p>

    <h2>I. Legal Framework and Platform Use</h2>

    <h4>1. Acceptance of Conditions</h4>
    <p>By clicking the "Submit" button (or any equivalent action), the User acknowledges having read, understood, and accepted the entirety of these general conditions. This action constitutes a valid electronic signature that legally binds the User to these provisions with the same force as a handwritten signature.</p>

    <h4>2. Platform Use</h4>
    <p>AcquizGroup operates exclusively as a sophisticated networking platform for business transactions. Users commit to providing accurate, current, and complete information, and must strictly comply with the established Professional Code of Conduct. Any use deemed fraudulent, abusive, or non-compliant with applicable laws and regulations may result in immediate suspension or permanent account termination, without prejudice to other remedies.</p>

    <h4>3. Confidentiality and Data Integrity</h4>
    <p>All information shared within the platform is classified as strictly confidential and shall only be used for the specified purposes of facilitating business acquisitions and transfers. Any documented breach of confidentiality may result in disciplinary sanctions, including account suspension or termination, and will not entitle the User to any refund.</p>

    <h4>4. Required Conduct</h4>
    <p>Users are required to demonstrate the highest standards of respect, courtesy, and professional integrity. Any inappropriate behavior, including harassment, insults, defamation, or any damage to the reputation of AcquizGroup or its members, is formally prohibited and may result in immediate and permanent expulsion from the platform without fee refund.</p>

    <h4>5. Service Termination</h4>
    <p>AcquizGroup reserves the right to suspend or terminate a User account immediately in case of substantial breach of these Conditions. Users may request account closure at any time; however, unless explicitly stated otherwise, no refund of prepaid fees will be due upon closure.</p>

    <h2>II. Liability and Legal Provisions</h2>

    <h4>6. Limitation of Liability</h4>
    <p>AcquizGroup acts solely as a facilitator and expressly offers no guarantee regarding the success, completion of financing, or conclusion of any transaction initiated through the platform. We cannot be held liable for decisions, agreements, or professional services provided by third-party participants (including Sellers, Buyers, Lenders, Consultants, or Internal Partners), nor for verbal or written communications conducted outside the platform's secure environment. We are also not responsible for service interruptions, technical errors, or circumstances constituting force majeure.</p>

    <h4>7. Governing Law and Jurisdiction</h4>
    <p>These conditions are governed by and interpreted in accordance with the laws of the State of Florida, United States. Any dispute arising therefrom shall be submitted to the exclusive jurisdiction of the courts located therein. In case of divergence or conflict between language versions, the English version shall prevail.</p>

    <h4>8. Verification Process and Due Diligence</h4>
    <p>To ensure a secure environment for financial transactions and sensitive data management, AcquizGroup subjects all Users with financial or legal roles (Lenders, Business Law Attorneys, CPAs, Tax Advisors, Notaries, and Consultants) to a rigorous background check process. This process is a mandatory condition for their admission and maintenance of status on the platform. For Lenders acting as individuals or unregulated entities (e.g., Private Lenders, Family Offices), the process will include background verification of their executives or key representatives. Despite this rigorous process, the User is solely responsible for their own due diligence and must remain vigilant. The User commits to immediately report to AcquizGroup any person or organization with practices deemed questionable or non-compliant with professional integrity standards.</p>

    <h2>ANNEX D – Lenders</h2>

    <h4>1. Admission and Status Maintenance</h4>
    <p>Integration and status maintenance are subject to successful completion of the background check process. For unregulated entities (Private Lenders, Family Offices), the Background Check will be conducted on executives or key representatives. The Lender commits to maintaining the accuracy of data submitted during registration (Lender Type, Financial Products, Criteria, etc.).</p>

    <h4>2. Confidentiality and Information Integrity</h4>
    <p>The Lender formally commits to guaranteeing the absolute and perpetual confidentiality of all Files, CIMs, and financial data submitted to them. Information received must only be used for the sole purpose of evaluating the possibility of submitting a Financing Offer.</p>

    <h4>3. Offer Submission and Process</h4>
    <p>A commitment to responsiveness is expected from the Lender. The Lender is responsible for formulating the most attractive offer, determining its conditions and eligibility requirements. Submitted offers will be displayed anonymously on the business listing page (Deal Monitor). The Lender's identity will only be revealed after signing a Letter of Intent (LOI).</p>

    <h4>4. Decision Independence and Logistics</h4>
    <p>AcquizGroup is a networking platform and will not intervene in any way in the Buyer's selection process. AcquizGroup cannot guarantee that the submitted offer will be selected. It is imperative to share your calendar and keep it updated for appointment scheduling.</p>

    <h4>5. Platform Management Fees and Closing Conditions</h4>
    <p><strong>Applicable Fees:</strong> Platform management fees equivalent to <strong>2%</strong> of the total transaction value (amount of the loan actually granted and disbursed or total value of the financed acquisition) will apply for each successful transaction concluded on this Platform. These fees are due by the Lender to AcquizGroup at the closing of the transaction (Closing).</p>

    <p><strong>Closing Transparency:</strong> Lenders are requested to share clear financing agreement information with the Buyer, as well as with AcquizGroup, to ensure a smooth and efficient Closing process. Failure to comply with this requirement may result in delays for which AcquizGroup will not be held responsible.</p>

    <p><strong>Fraud Sanctions:</strong> To ensure proper transaction conduct, any fraudulent practice, any attempt to conceal financing agreements from the platform, hide information, or any practice deemed dishonest will be subject to disciplinary sanctions, including immediate and permanent expulsion from the Platform without refund of fees incurred.</p>
  `,
  fr: `
    <p class="text-sm text-gray-600 mb-4"><strong>Version: 2025-09-17 Rév. E1</strong></p>

    <h2>I. Cadre Juridique et Utilisation de la Plateforme</h2>

    <h4>1. Acceptation des Conditions</h4>
    <p>En cliquant sur le bouton «Soumettre» (ou toute action équivalente), l'Utilisateur reconnaît avoir lu, compris et accepté l'intégralité des présentes conditions générales. Cette action constitue une signature électronique valide qui lie juridiquement l'Utilisateur à ces dispositions avec la même force qu'une signature manuscrite.</p>

    <h4>2. Utilisation de la Plateforme</h4>
    <p>AcquizGroup opère exclusivement comme une plateforme de réseautage sophistiquée pour les transactions commerciales. Les utilisateurs s'engagent à fournir des informations exactes, actuelles et complètes, et doivent se conformer strictement au Code de Conduite Professionnelle établi. Toute utilisation jugée frauduleuse, abusive ou non conforme aux lois et réglementations applicables peut entraîner une suspension immédiate ou la résiliation permanente du compte, sans préjudice des autres recours.</p>

    <h4>3. Confidentialité et Intégrité des Données</h4>
    <p>Toutes les informations partagées au sein de la plateforme sont classées comme strictement confidentielles et ne doivent être utilisées que pour les fins spécifiées de facilitation des acquisitions et transferts d'entreprises. Toute violation documentée de la confidentialité peut entraîner des sanctions disciplinaires, y compris la suspension ou la résiliation du compte, et ne donnera droit à aucun remboursement.</p>

    <h4>4. Conduite Requise</h4>
    <p>Les utilisateurs sont tenus de démontrer les normes les plus élevées de respect, de courtoisie et d'intégrité professionnelle. Tout comportement inapproprié, y compris le harcèlement, les insultes, la diffamation ou tout dommage à la réputation d'AcquizGroup ou de ses membres, est formellement interdit et peut entraîner une expulsion immédiate et permanente de la plateforme sans remboursement des frais.</p>

    <h4>5. Résiliation du Service</h4>
    <p>AcquizGroup se réserve le droit de suspendre ou de résilier immédiatement un compte utilisateur en cas de violation substantielle de ces Conditions. Les utilisateurs peuvent demander la fermeture de leur compte à tout moment; cependant, sauf indication contraire explicite, aucun remboursement des frais prépayés ne sera dû lors de la fermeture.</p>

    <h2>II. Responsabilité et Dispositions Légales</h2>

    <h4>6. Limitation de Responsabilité</h4>
    <p>AcquizGroup agit uniquement en tant que facilitateur et n'offre expressément aucune garantie concernant le succès, l'achèvement du financement ou la conclusion de toute transaction initiée via la plateforme. Nous ne pouvons être tenus responsables des décisions, accords ou services professionnels fournis par des participants tiers (y compris les Vendeurs, Acheteurs, Prêteurs, Consultants ou Partenaires Internes), ni des communications verbales ou écrites menées en dehors de l'environnement sécurisé de la plateforme. Nous ne sommes pas non plus responsables des interruptions de service, des erreurs techniques ou des circonstances constituant un cas de force majeure.</p>

    <h4>7. Loi Applicable et Juridiction</h4>
    <p>Ces conditions sont régies et interprétées conformément aux lois de l'État de Floride, États-Unis. Tout différend en découlant sera soumis à la juridiction exclusive des tribunaux qui y sont situés. En cas de divergence ou de conflit entre les versions linguistiques, la version anglaise prévaudra.</p>

    <h4>8. Processus de Vérification et Diligence Raisonnable</h4>
    <p>Pour assurer un environnement sécurisé pour les transactions financières et la gestion des données sensibles, AcquizGroup soumet tous les Utilisateurs ayant des rôles financiers ou juridiques (Prêteurs, Avocats en Droit des Affaires, CPA, Conseillers Fiscaux, Notaires et Consultants) à un processus rigoureux de vérification des antécédents. Ce processus est une condition obligatoire pour leur admission et le maintien de leur statut sur la plateforme. Pour les Prêteurs agissant en tant qu'individus ou entités non réglementées (ex: Prêteurs Privés, Family Offices), le processus inclura la vérification des antécédents de leurs dirigeants ou représentants clés. Malgré ce processus rigoureux, l'Utilisateur est seul responsable de sa propre diligence raisonnable et doit rester vigilant. L'Utilisateur s'engage à signaler immédiatement à AcquizGroup toute personne ou organisation dont les pratiques sont jugées douteuses ou non conformes aux normes d'intégrité professionnelle.</p>

    <h2>ANNEXE D – Prêteurs</h2>

    <h4>1. Admission et Maintien du Statut</h4>
    <p>L'intégration et le maintien du statut sont soumis à la réussite du processus de vérification des antécédents. Pour les entités non réglementées (Prêteurs Privés, Family Offices), la Vérification des Antécédents sera effectuée sur les dirigeants ou représentants clés. Le Prêteur s'engage à maintenir l'exactitude des données soumises lors de l'inscription (Type de Prêteur, Produits Financiers, Critères, etc.).</p>

    <h4>2. Confidentialité et Intégrité des Informations</h4>
    <p>Le Prêteur s'engage formellement à garantir la confidentialité absolue et perpétuelle de tous les Dossiers, CIM et données financières qui lui sont soumis. Les informations reçues ne doivent être utilisées que dans le seul but d'évaluer la possibilité de soumettre une Offre de Financement.</p>

    <h4>3. Soumission d'Offre et Processus</h4>
    <p>Un engagement de réactivité est attendu du Prêteur. Le Prêteur est responsable de formuler l'offre la plus attractive, de déterminer ses conditions et exigences d'éligibilité. Les offres soumises seront affichées anonymement sur la page de l'annonce commerciale (Deal Monitor). L'identité du Prêteur ne sera révélée qu'après la signature d'une Lettre d'Intention (LOI).</p>

    <h4>4. Indépendance Décisionnelle et Logistique</h4>
    <p>AcquizGroup est une plateforme de réseautage et n'interviendra en aucun cas dans le processus de sélection de l'Acheteur. AcquizGroup ne peut garantir que l'offre soumise sera sélectionnée. Il est impératif de partager votre calendrier et de le maintenir à jour pour la planification des rendez-vous.</p>

    <h4>5. Frais de Gestion de Plateforme et Conditions de Clôture</h4>
    <p><strong>Frais Applicables:</strong> Des frais de gestion de plateforme équivalents à <strong>2%</strong> de la valeur totale de la transaction (montant du prêt effectivement accordé et décaissé ou valeur totale de l'acquisition financée) s'appliqueront pour chaque transaction réussie conclue sur cette Plateforme. Ces frais sont dus par le Prêteur à AcquizGroup à la clôture de la transaction (Closing).</p>

    <p><strong>Transparence de Clôture:</strong> Les Prêteurs sont priés de partager des informations claires sur l'accord de financement avec l'Acheteur, ainsi qu'avec AcquizGroup, pour assurer un processus de Clôture fluide et efficace. Le non-respect de cette exigence peut entraîner des retards dont AcquizGroup ne sera pas tenu responsable.</p>

    <p><strong>Sanctions pour Fraude:</strong> Pour assurer le bon déroulement des transactions, toute pratique frauduleuse, toute tentative de dissimuler des accords de financement à la plateforme, de cacher des informations ou toute pratique jugée malhonnête sera soumise à des sanctions disciplinaires, y compris l'expulsion immédiate et permanente de la Plateforme sans remboursement des frais engagés.</p>
  `,
};

// Form Data Type
export interface LenderRegistrationFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  address: string;
  phoneNumber: string;
  lenderTypes: string[];
  loanProductTypes: string[];
  collateralOptions: string[];
  minRange: string;
  maxRange: string;
  minGrossRevenue: string;
  maxGrossRevenue: string;
  minEbitda: string;
  maxEbitda: string;
  minCashFlow: string;
  maxCashFlow: string;
  debtServiceCoverageRatio: string;
  creditScoreRequired: string | null;
  foreignCreditScoreAccepted: string | null;
  creditScoreCountries_canada: string[];
  creditScoreCountries_usa: string[];
  creditScoreCountries_europe: string[];
  creditScoreCountries_others: string[];
  foreignCollateralAccepted: string | null;
  foreignCollateralOptions: string[];
  jurisdiction_canada: string[];
  jurisdiction_usa: string[];
  jurisdiction_europe: string[];
  jurisdiction_others: string[];
  agreeTerms: boolean;
  backgroundCheckAuth: boolean;
  howHeard: string[];
}

export const INITIAL_FORM_DATA: LenderRegistrationFormData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  address: '',
  phoneNumber: '',
  lenderTypes: [],
  loanProductTypes: [],
  collateralOptions: [],
  minRange: '',
  maxRange: '',
  minGrossRevenue: '',
  maxGrossRevenue: '',
  minEbitda: '',
  maxEbitda: '',
  minCashFlow: '',
  maxCashFlow: '',
  debtServiceCoverageRatio: '',
  creditScoreRequired: null,
  foreignCreditScoreAccepted: null,
  creditScoreCountries_canada: [],
  creditScoreCountries_usa: [],
  creditScoreCountries_europe: [],
  creditScoreCountries_others: [],
  foreignCollateralAccepted: null,
  foreignCollateralOptions: [],
  jurisdiction_canada: [],
  jurisdiction_usa: [],
  jurisdiction_europe: [],
  jurisdiction_others: [],
  agreeTerms: false,
  backgroundCheckAuth: false,
  howHeard: [],
};
